import { Fragment, useEffect, useRef, useState } from "react";
import { listStages } from "../../content/stages.js";
import { useGame } from "../../app/GameProvider.js";
import { loadStageProgress } from "../../infrastructure/playLogApi.js";
import { StageCard } from "./StageCard.js";
import { startStageCommand } from "../../domain/startStage.js";
import { bringInDividerIndex } from "./bringInDivider.js";
import { clearRateRange } from "./clearRate.js";
import { getBgmVolume, getSeVolume, setSeVolume } from "../../audio/mixSettings.js";
import { setGameBgmVolume } from "../../audio/bgm/bgmPlayers.js";
import { playGameSound } from "../../audio/gameSoundPlayer.js";
import { countClearedStages, nextBringInReward } from "../../domain/deck/bringIn.js";
import { getCard } from "../../content/lookups.js";
import { CustomPlayPanel } from "./CustomPlayPanel.js";
import { StageSelectTabs, type StageSelectTab } from "./StageSelectTabs.js";

const SE_PREVIEW_DURATION_MS = 140;

export function StageSelectScreen() {
  const { dispatch } = useGame();
  const [progressByStage, setProgressByStage] = useState(() => new Map<string, Awaited<ReturnType<typeof loadStageProgress>>[number]>());
  const [tab, setTab] = useState<StageSelectTab>("catalog");
  const [audioMenuOpen, setAudioMenuOpen] = useState(false);
  const [bgmVolume, setBgmVolume] = useState(getBgmVolume);
  const [seVolume, setSeVolumeValue] = useState(getSeVolume);
  const sePreviewPlayingRef = useRef(false);
  const sePreviewTimerRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadStageProgress().then((stages) => {
      if (!cancelled) setProgressByStage(new Map(stages.map((stage) => [stage.stageId, stage])));
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => () => {
    if (sePreviewTimerRef.current !== null) window.clearTimeout(sePreviewTimerRef.current);
  }, []);

  const stages = listStages();
  const range = clearRateRange(stages.map((stage) => progressByStage.get(stage.id)));
  const dividerIndex = bringInDividerIndex(stages);
  const clearedCount = countClearedStages([...progressByStage.values()]);
  const nextReward = nextBringInReward(clearedCount);
  const nextRewardName = nextReward ? getCard(nextReward.cardId)?.name : null;
  const setBgm = (value: number) => {
    const next = setGameBgmVolume(value);
    setBgmVolume(next);
  };
  const setSe = (value: number) => {
    const next = setSeVolume(value);
    setSeVolumeValue(next);
    if (sePreviewPlayingRef.current) return;
    sePreviewPlayingRef.current = true;
    playGameSound("ui.select");
    sePreviewTimerRef.current = window.setTimeout(() => {
      sePreviewPlayingRef.current = false;
      sePreviewTimerRef.current = null;
    }, SE_PREVIEW_DURATION_MS);
  };

  return (
    <section className="screen screen-stage-select">
      <header className="stage-select-header">
        <div>
          <p className="stage-select-kicker">SELECT STAGE</p>
          <h1>ステージを選ぶ</h1>
        </div>
        <div className="stage-select-tools">
          <button
            type="button"
            className="btn stage-audio-button"
            aria-label="音量"
            aria-expanded={audioMenuOpen}
            aria-controls="stage-audio-menu"
            onClick={() => setAudioMenuOpen((open) => !open)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 10v4h4l5 4V6L8 10H4Z" />
              <path d="M16 9.5a4 4 0 0 1 0 5M18.5 7a7.5 7.5 0 0 1 0 10" />
            </svg>
          </button>
          <button type="button" className="btn stage-patch-notes-button" onClick={() => dispatch({ type: "OPEN_OVERLAY", overlay: "patchNotes" })}>
            <img src="/icon-patch-notes.svg" alt="" />
            更新内容
          </button>
          {audioMenuOpen ? (
            <section id="stage-audio-menu" className="stage-audio-menu" aria-label="音量調整">
              <p>音量</p>
              <label>
                <span>BGM <output>{Math.round(bgmVolume * 100)}%</output></span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={Math.round(bgmVolume * 100)}
                  onChange={(event) => setBgm(Number(event.target.value) / 100)}
                  aria-label="BGM音量"
                />
              </label>
              <label>
                <span>SE <output>{Math.round(seVolume * 100)}%</output></span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={Math.round(seVolume * 100)}
                  onChange={(event) => setSe(Number(event.target.value) / 100)}
                  aria-label="SE音量"
                />
              </label>
              <small>SEは調整時に試聴できます</small>
            </section>
          ) : null}
        </div>
      </header>
      <StageSelectTabs value={tab} onChange={setTab} />
      {tab === "custom" ? <CustomPlayPanel dispatch={dispatch} /> : (
      <ul className="stage-list">
        {stages.map((stage, index) => (
          <Fragment key={stage.id}>
            {dividerIndex === index ? (
              <li className="stage-bring-in-section">
                <div className="stage-bring-in-divider">
                  <span>ここから作戦会議あり</span>
                </div>
                <section className="stage-next-reward" aria-label="持ち込み報酬の進捗">
                  {nextReward && nextRewardName ? (
                    <>
                      <span className="stage-next-reward-label">次の報酬</span>
                      <strong className="stage-next-reward-name">{nextRewardName}</strong>
                      <span className="stage-next-reward-count"><b>{clearedCount}</b> / {nextReward.unlockAt} クリア</span>
                      <span className="stage-next-reward-remaining">あと{nextReward.unlockAt - clearedCount}ステージ</span>
                      <div
                        className="stage-next-reward-meter"
                        role="progressbar"
                        aria-label={`${nextRewardName}の解放進捗`}
                        aria-valuemin={0}
                        aria-valuemax={nextReward.unlockAt}
                        aria-valuenow={clearedCount}
                      >
                        <span style={{ width: `${clearedCount / nextReward.unlockAt * 100}%` }} />
                      </div>
                    </>
                  ) : (
                    <strong className="stage-next-reward-complete">持ち込み報酬をすべて獲得済み</strong>
                  )}
                </section>
              </li>
            ) : null}
            <li>
              <StageCard
                stage={stage}
                progress={progressByStage.get(stage.id)}
                range={range}
                onStart={() => dispatch(startStageCommand(stage.id))}
                index={index}
              />
            </li>
          </Fragment>
        ))}
      </ul>
      )}
      <button type="button" className="btn" onClick={() => dispatch({ type: "BACK_TO_TITLE" })}>
        戻る
      </button>
    </section>
  );
}
